import { ReadonlySignal } from '@preact/signals-react';
import { injectable } from 'inversify';

import { AuthState } from '@vp/auth/core/interface/AuthState';
import { OperationResult } from '@vp/common/core/OperationResult';
import { LoadProfileDto } from '@vp/profile/core/dto/LoadProfileDto';
import { ProfilePort } from '@vp/profile/core/interface/ProfilePort';
import { ProfileLoadingState, ProfileState } from '@vp/profile/core/interface/ProfileState';
import { ProfileModel } from '@vp/profile/core/model/ProfileModel';
import { ActivateProfileUsecase } from '@vp/profile/core/usecase/ActivateProfileUsecase';
import { LoadProfileUsecase } from '@vp/profile/core/usecase/LoadProfileUsecase';
import { UnlockProfileUsecase } from '@vp/profile/core/usecase/UnlockProfileUsecase';
import { ScheduleSlideshowUsecase } from '@vp/slideshow/core/usecase/ScheduleSlideshowUsecase';

@injectable()
export class ProfileFacade implements ProfilePort {
  readonly loadingState: ReadonlySignal<ProfileLoadingState> = this.profileState.loadingState;
  readonly profile: ReadonlySignal<ProfileModel> = this.profileState.profile;
  readonly canActivateProfile: ReadonlySignal<boolean> = this.authState.authenticated;
  readonly profileForActivation: ReadonlySignal<string | null> = this.profileState.profileForActivation;

  constructor(
    private readonly authState: AuthState,
    private readonly profileState: ProfileState,
    private readonly loadProfileUsecase: LoadProfileUsecase,
    private readonly activateProfileUsecase: ActivateProfileUsecase,
    private readonly scheduleSlideshowUsecase: ScheduleSlideshowUsecase,
    private readonly unlockProfileUsecase: UnlockProfileUsecase,
  ) {}

  async scheduleSlideshow(): Promise<void> {
    await this.scheduleSlideshowUsecase.execute(this.profileState.profile.value);
  }

  async setProfile(profile: ProfileModel): Promise<void> {
    this.profileState.setProfile(profile);
  }

  async unlockProfile(id: string, code: string, constroller?: AbortController): Promise<OperationResult<ProfileModel>> {
    return this.unlockProfileUsecase.execute(id, code, constroller);
  }

  async loadProfile(dto: LoadProfileDto): Promise<void> {
    await this.loadProfileUsecase.execute(dto);
    await this.scheduleSlideshow();
  }

  async activateProfile(id: string, controller?: AbortController): Promise<OperationResult> {
    return await this.activateProfileUsecase.execute(id, controller);
  }

  storeProfileForActivation(id: string): void {
    this.profileState.storeProfileForActivation(id);
  }

  resetProfileForActivation(): void {
    this.profileState.resetProfileForActivation();
  }
}
